import React, { useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon, InsertDriveFile as FileIcon } from '@mui/icons-material';
import { colors } from '../styles/theme';
import { formatFileSize } from '../utils/fileUtils';

export interface FileUploadZoneRef {
  clearInput: () => void;
}

export interface FileUploadZoneProps {
  /** Accepted file types (e.g. ".pdf,.doc,.docx" or ".csv,.xlsx,.xls") */
  accept: string;
  /** Text shown under "or click to browse" (e.g. "Supported formats: PDF, DOCX") */
  supportedFormatsText: string;
  /** Currently selected file, or null */
  file: File | null;
  /** Called when user selects a file (via input or drop) */
  onFileSelect: (file: File | null) => void;
  /** Show progress bar and disable zone */
  isUploading?: boolean;
  /** 0–100 progress value when isUploading */
  uploadProgress?: number;
  /** Optional content rendered above the drop zone (e.g. client name field) */
  children?: React.ReactNode;
}

const FileUploadZone = forwardRef<FileUploadZoneRef, FileUploadZoneProps>(function FileUploadZone(
  {
    accept,
    supportedFormatsText,
    file,
    onFileSelect,
    isUploading = false,
    uploadProgress = 0,
    children,
  },
  ref
) {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    clearInput: () => {
      if (inputRef.current) inputRef.current.value = '';
    },
  }));

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.files?.[0] ?? null;
      onFileSelect(next);
    },
    [onFileSelect]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const next = event.dataTransfer.files?.[0] ?? null;
      onFileSelect(next);
    },
    [onFileSelect]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleClick = useCallback(() => {
    if (!isUploading) inputRef.current?.click();
  }, [isUploading]);

  return (
    <>
      <input
        type="file"
        ref={inputRef}
        onChange={handleInputChange}
        accept={accept}
        style={{ display: 'none' }}
      />

      {children}

      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClick}
        sx={{
          border: '2px dashed',
          borderColor: colors.border.light,
          borderRadius: 1,
          p: 6,
          textAlign: 'center',
          cursor: isUploading ? 'wait' : 'pointer',
          backgroundColor: file ? `${colors.primaryLight}15` : colors.background.page,
          transition: 'all 0.2s ease',
          opacity: isUploading ? 0.85 : 1,
          pointerEvents: isUploading ? 'none' : 'auto',
          '&:hover': isUploading
            ? {}
            : {
                backgroundColor: `${colors.primaryLight}12`,
                borderColor: colors.primaryLight,
              },
        }}
      >
        {file ? (
          <Box>
            <FileIcon sx={{ fontSize: 64, color: colors.primaryLight, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.text.primary, mb: 1 }}>
              {file.name}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {formatFileSize(file.size)}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.primaryLight, mt: 2 }}>
              Click to select a different file
            </Typography>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 64, color: colors.primaryLight, mb: 2 }} />
            <Typography variant="h6" sx={{ color: colors.text.primary, mb: 1 }}>
              Drag and drop your file here
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 2 }}>
              or click to browse
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.disabled }}>
              {supportedFormatsText}
            </Typography>
          </Box>
        )}
      </Box>

      {isUploading && (
        <Box sx={{ mt: 3 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: colors.border.medium,
              '& .MuiLinearProgress-bar': {
                backgroundColor: colors.primaryLight,
                borderRadius: 4,
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, mt: 1, textAlign: 'center' }}
          >
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}
    </>
  );
});

export default FileUploadZone;
